import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../api/client";
import { fetchSettings } from "../api/settings";
import { playLoop, playOnce, stopAllCallSounds, stopLoop, unlockAudio } from "../api/sound";
import { startVibration, stopVibration } from "../api/sound";
import { ru } from "../locales/ru";
import { useAuth } from "./AuthContext";
import { useChat } from "./ChatContext";

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { user } = useAuth();
  const { send, subscribe } = useChat();
  const [call, setCall] = useState(null);
  const [peer, setPeer] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [media, setMedia] = useState("audio");
  const [cameraOn, setCameraOn] = useState(false);
  const [ringSound, setRingSound] = useState(true);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioRef = useRef(null);
  const pendingIceRef = useRef([]);
  const restartedRef = useRef(false);
  const callRef = useRef(null);
  const iceConfigRef = useRef(null);
  const mediaRef = useRef("audio");
  const facingRef = useRef("user");
  const ringSoundRef = useRef(true);
  const answeredRef = useRef(false);
  callRef.current = call;
  mediaRef.current = media;

  const teardown = useCallback(() => {
    stopAllCallSounds();
    if (pcRef.current) {
      try {
        pcRef.current.onicecandidate = null;
        pcRef.current.ontrack = null;
        pcRef.current.close();
      } catch {}
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (audioRef.current) audioRef.current.srcObject = null;
    pendingIceRef.current = [];
    restartedRef.current = false;
    facingRef.current = "user";
    answeredRef.current = false;
    setCall(null);
    setPeer(null);
    setPhase("idle");
    setMuted(false);
    setMedia("audio");
    setCameraOn(false);
    setLocalStream(null);
    setRemoteStream(null);
  }, []);

  const iceConfig = useCallback(async () => {
    if (!iceConfigRef.current) {
      try {
        const { data } = await api.get("/calls/ice-config");
        iceConfigRef.current = data;
      } catch {
        iceConfigRef.current = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };
      }
    }
    return iceConfigRef.current;
  }, []);

  const getMedia = useCallback(async (wantVideo) => {
    if (localStreamRef.current) return localStreamRef.current;
    const constraints = wantVideo
      ? { audio: true, video: { facingMode: facingRef.current, width: { ideal: 1280 } } }
      : { audio: true, video: false };
    let stream;
    if (wantVideo) {
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (err) {
        if (err?.name === "NotAllowedError" || err?.name === "NotFoundError") {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
          toast.info(ru.calls.cameraFallback);
        } else {
          throw err;
        }
      }
    } else {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    }
    localStreamRef.current = stream;
    setLocalStream(stream);
    setCameraOn(stream.getVideoTracks().length > 0);
    return stream;
  }, []);

  const makeOffer = useCallback(
    async (callId, iceRestart = false) => {
      const pc = pcRef.current;
      if (!pc) return;
      const offer = await pc.createOffer({ iceRestart });
      await pc.setLocalDescription(offer);
      send("sdp", { call_id: callId, description: { type: offer.type, sdp: offer.sdp } });
    },
    [send]
  );

  const ensurePeerConnection = useCallback(
    async (callId) => {
      if (pcRef.current) return pcRef.current;
      const pc = new RTCPeerConnection(await iceConfig());
      pcRef.current = pc;
      const stream = await getMedia(mediaRef.current === "video");
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) send("ice_candidate", { call_id: callId, candidate: e.candidate.toJSON() });
      };
      pc.ontrack = (e) => {
        setRemoteStream(e.streams[0]);
        if (mediaRef.current !== "video" && audioRef.current) {
          audioRef.current.srcObject = e.streams[0];
          audioRef.current.play().catch(() => {});
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState !== "failed") return;
        const current = callRef.current;
        const amCaller = current && current.caller_id === user?.id;
        if (!restartedRef.current && amCaller) {
          restartedRef.current = true;
          makeOffer(callId, true).catch(() => {});
        } else {
          toast.error(ru.calls.connectionLost);
          if (current) send("call_end", { call_id: current.id });
          teardown();
        }
      };
      return pc;
    },
    [getMedia, iceConfig, makeOffer, send, teardown, user?.id]
  );

  const flushIce = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    const queued = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const candidate of queued) {
      await pc.addIceCandidate(candidate).catch(() => {});
    }
  }, []);

  const startCall = useCallback(
    async (target, wantMedia = "audio") => {
      if (phase !== "idle") return;
      mediaRef.current = wantMedia;
      try {
        await getMedia(wantMedia === "video");
      } catch {
        toast.error(wantMedia === "video" ? ru.calls.mediaDenied : ru.calls.micDenied);
        teardown();
        return;
      }
      const ok = send("call_initiate", { callee_id: target.id, media: wantMedia });
      if (!ok) {
        toast.error(ru.calls.noConnection);
        teardown();
        return;
      }
      setMedia(wantMedia);
      setPeer(target);
      setPhase("outgoing");
    },
    [getMedia, phase, send, teardown]
  );

  const acceptCall = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    mediaRef.current = current.media || "audio";
    try {
      await getMedia(mediaRef.current === "video");
    } catch {
      toast.error(ru.calls.micDenied);
      send("call_decline", { call_id: current.id });
      teardown();
      return;
    }
    send("call_accept", { call_id: current.id });
  }, [getMedia, send, teardown]);

  const declineCall = useCallback(() => {
    const current = callRef.current;
    if (current) send("call_decline", { call_id: current.id });
    teardown();
  }, [send, teardown]);

  const hangUp = useCallback(() => {
    const current = callRef.current;
    if (current) send("call_end", { call_id: current.id });
    if (answeredRef.current) playOnce("end");
    teardown();
  }, [send, teardown]);

  const applyRingSound = useCallback((enabled) => {
    ringSoundRef.current = enabled;
    setRingSound(enabled);
    if (!enabled) {
      stopLoop("ringtone");
      stopLoop("dialtone");
    }
  }, []);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
    setMuted(next);
  }, [muted]);

  const toggleCamera = useCallback(() => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCameraOn(track.enabled);
  }, []);

  const switchCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    const pc = pcRef.current;
    const oldTrack = stream?.getVideoTracks()[0];
    if (!stream || !pc || !oldTrack) return;
    const next = facingRef.current === "user" ? "environment" : "user";
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({ video: { facingMode: next }, audio: false });
      const newTrack = fresh.getVideoTracks()[0];
      const sender = pc.getSenders().find((s) => s.track && s.track.kind === "video");
      if (sender) await sender.replaceTrack(newTrack);
      stream.removeTrack(oldTrack);
      oldTrack.stop();
      stream.addTrack(newTrack);
      facingRef.current = next;
      setLocalStream(stream);
      setCameraOn(newTrack.enabled);
    } catch {
      toast.info(ru.calls.switchUnavailable);
    }
  }, []);


  useEffect(() => {
    if (!subscribe || !user) return;
    return subscribe(async (ev) => {
      const { event, data } = ev;
      if (event === "call_created") {
        setCall(data.call);
        setPeer(data.peer);
        setPhase("outgoing");
      } else if (event === "call_incoming") {
        if (callRef.current) return;
        mediaRef.current = data.call.media || "audio";
        setMedia(mediaRef.current);
        setCall(data.call);
        setPeer(data.caller);
        setPhase("incoming");
      } else if (event === "call_accepted") {
        mediaRef.current = data.call.media || "audio";
        setMedia(mediaRef.current);
        stopAllCallSounds();
        answeredRef.current = true;
        setCall(data.call);
        setPhase("active");
        try {
          await ensurePeerConnection(data.call.id);
          if (data.call.caller_id === user.id) await makeOffer(data.call.id);
        } catch {
          toast.error(ru.calls.failed);
          send("call_end", { call_id: data.call.id });
          teardown();
        }
      } else if (event === "sdp") {
        const pc = await ensurePeerConnection(data.call_id).catch(() => null);
        if (!pc) return;
        const desc = data.description;
        await pc.setRemoteDescription(desc).catch(() => {});
        await flushIce();
        if (desc.type === "offer") {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send("sdp", { call_id: data.call_id, description: { type: answer.type, sdp: answer.sdp } });
        }
      } else if (event === "ice_candidate") {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) {
          await pc.addIceCandidate(data.candidate).catch(() => {});
        } else {
          pendingIceRef.current.push(data.candidate);
        }
      } else if (event === "call_declined") {
        toast.info(ru.calls.declinedByPeer);
        playOnce("end");
        teardown();
      } else if (event === "call_busy") {
        toast.info(ru.calls.busy);
        playOnce("end");
        teardown();
      } else if (event === "call_ended") {
        const reason = data.reason;
        if (reason === "missed" || reason === "cancelled") toast.info(ru.calls.missed);
        else if (reason === "connection_lost" || reason === "peer_disconnected") toast.error(ru.calls.connectionLost);
        if (answeredRef.current || reason === "hangup") playOnce("end");
        teardown();
      } else if (event === "call_error") {
        toast.error(data.message || ru.errors.generic);
        teardown();
      }
    });
  }, [subscribe, user?.id, ensurePeerConnection, makeOffer, flushIce, send, teardown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user && phase !== "idle") teardown();
  }, [user, phase, teardown]);

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchSettings()
      .then((s) => {
        ringSoundRef.current = s.ring_sound;
        setRingSound(s.ring_sound);
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === "incoming") {
      if (ringSoundRef.current) playLoop("ringtone");
      startVibration([600, 400]);
      return () => {
        stopLoop("ringtone");
        stopVibration();
      };
    }
    if (phase === "outgoing") {
      if (ringSoundRef.current) playLoop("dialtone");
      return () => stopLoop("dialtone");
    }
    stopAllCallSounds();
    return undefined;
  }, [phase, ringSound]);

  return (
    <CallContext.Provider
      value={{
        call, peer, phase, muted, media, cameraOn, localStream, remoteStream, ringSound,
        startCall, acceptCall, declineCall, hangUp, toggleMute, toggleCamera, switchCamera, applyRingSound,
      }}
    >
      {children}
      <audio ref={audioRef} autoPlay playsInline data-testid="remote-audio" />
    </CallContext.Provider>
  );
}

export function useCall() {
  return useContext(CallContext);
}
